import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, CheckCircle, Lightbulb, ShieldWarning, ChartBar, AndroidLogo, Calendar, Gear, CircleNotch } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { createPortal } from 'react-dom';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import { CHAPTER_STEPS } from './tutorialChapterSteps';
import { getCalApi } from "@calcom/embed-react";

const TutorialOverlay = ({ isActive, onComplete, onSkip, chapter }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [isAdvancing, setIsAdvancing] = useState(false);

  // Simple state for tracking input validation
  const [inputValues, setInputValues] = useState({});
  const [isActionReady, setIsActionReady] = useState(false);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggedPosition, setDraggedPosition] = useState(null);
  const dragRef = useRef(null);

  // Cal.com booking state
  const [isBookingCall, setIsBookingCall] = useState(false);

  // Track user's choice for conditional step display
  const [userChoice, setUserChoice] = useState(null);

  // Get steps for current chapter
  const steps = chapter ? CHAPTER_STEPS[chapter.id] || [] : [];

  // Function to find next valid step based on conditional display
  const findNextValidStep = (fromStep) => {
    for (let i = fromStep + 1; i < steps.length; i++) {
      const step = steps[i];
      // If step has showOnlyForChoice, check if it matches user's choice
      if (step.showOnlyForChoice) {
        if (userChoice === step.showOnlyForChoice) {
          return i;
        }
        // Skip this step if it doesn't match the choice
        continue;
      }
      // Return first step without conditional display
      return i;
    }
    return -1; // No more valid steps
  };

  const currentStepData = steps[currentStep];

  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      initializeAccount();
      initializeCal();
    }
  }, [isActive]);

  const initializeAccount = async () => {
    try {
      const { getUserAccountId } = await import('../../utils/accountUtils');
      const { accountId: userAccountId } = await getUserAccountId();
      setAccountId(userAccountId);
    } catch (error) {
      console.error('Error getting account ID:', error);
    }
  };

  // Initialize Cal.com
  const initializeCal = async () => {
    try {
      const calApi = await getCalApi({"namespace":"ai-for-laundromats-onboarding"});
      calApi("ui", {"hideEventTypeDetails":false,"layout":"month_view"});

      // Set up booking success callback
      calApi("on", {
        action: "bookingSuccessful", 
        callback: (e) => {
          setIsBookingCall(false);

          // Update meeting booking status in database
          if (accountId) {
            updateMeetingBookingStatus();
          }

          // For Cal.com booking completion, explicitly find the experiment-while-waiting step
          const experimentStepIndex = steps.findIndex(step => step.id === 'experiment-while-waiting');

          if (experimentStepIndex !== -1) {
            setCurrentStep(experimentStepIndex);
          } else {
            // Fallback to generic next step logic
            const nextStep = findNextValidStep(currentStep);

            if (nextStep !== -1) {
              setCurrentStep(nextStep);
            } else {
              handleComplete();
            }
          }
        }
      });

    } catch (error) {
      console.error("Cal API initialization error:", error);
    }
  };

  // Handle Cal.com booking click
  const handleCalBookingClick = async (choiceId = 'schedule-call') => {
    setIsBookingCall(true);

    // Ensure user choice is set immediately for the callback
    setUserChoice(choiceId);

    try {
      const calApi = await getCalApi({"namespace":"ai-for-laundromats-onboarding"});

      // Open the booking modal directly
      calApi("modal", {
        calLink: "caleb-benedict/ai-for-laundromats-onboarding",
        config: {
          layout: "month_view"
        }
      });

    } catch (error) {
      console.error("Error opening Cal.com modal:", error);
      setIsBookingCall(false);
      // Fallback to direct link
      window.open('https://cal.com/caleb-benedict/ai-for-laundromats-onboarding', '_blank');
    }
  };

  // Update meeting booking status
  const updateMeetingBookingStatus = async () => {
    if (!accountId) return;

    try {
      const { error } = await supabase
        .from('onboarding_data')
        .upsert({
          account_id: accountId,
          meeting_booked: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'account_id'
        });

      if (error) {
        console.error('Error updating meeting booking status:', error);
      }
    } catch (error) {
      console.error('Error updating meeting booking status:', error);
    }
  };

  // Reset input tracking when step changes
  useEffect(() => {
    setInputValues({});
    setIsActionReady(false);
  }, [currentStep]);

  // Simple action readiness logic
  useEffect(() => {
    if (!currentStepData?.requiresAction) {
      setIsActionReady(true);
      return;
    }

    const { actionType, minCharacters } = currentStepData;

    if (actionType === 'type' && minCharacters) {
      const targetInput = document.querySelector(currentStepData.target);
      const domValue = targetInput ? (targetInput.value || '') : '';
      const storedValue = inputValues.inputValue || '';
      const actualValue = domValue || storedValue;

      setIsActionReady(actualValue.length >= minCharacters);
    } else {
      // For click actions, always ready
      setIsActionReady(true);
    }
  }, [currentStepData, inputValues]);

  // Simple event listener for actions
  useEffect(() => {
    if (!currentStepData?.requiresAction || !currentStepData?.target || isAdvancing) return;

    const element = document.querySelector(currentStepData.target);
    if (!element) {
      return;
    }

    const handleActionComplete = async () => {
      if (isAdvancing) return;

      setIsAdvancing(true);

      const nextStep = findNextValidStep(currentStep);
      if (nextStep !== -1) {
        setCurrentStep(nextStep);
      } else {
        try {
          if (accountId && chapter) {
            supabase
              .from('brain_tutorial_progress')
              .upsert({
                account_id: accountId,
                chapter_id: chapter.id,
                completed_at: new Date().toISOString()
              }, {
                onConflict: 'account_id,chapter_id'
              });
          }
        } catch (error) {
          console.error('Error marking chapter as complete:', error);
        }

        setIsVisible(false);
        setTimeout(() => onComplete(), 300);
      }

      setTimeout(() => setIsAdvancing(false), 1000);
    };

    const handleInputChange = (e) => {
      const value = e.target.value;
      const { actionType, minCharacters } = currentStepData;

      if (actionType === 'type' && minCharacters) {
        setInputValues(prev => ({ ...prev, inputValue: value }));

        if (value.length >= minCharacters) {
          setIsActionReady(true);
        } else {
          setIsActionReady(false);
        }
      }
    };

    // Add event listeners based on action type
    if (currentStepData.actionType === 'click') {
      element.addEventListener('click', handleActionComplete);
    } else if (currentStepData.actionType === 'type') {
      element.addEventListener('input', handleInputChange);
      element.addEventListener('keyup', handleInputChange);
      element.addEventListener('change', handleInputChange);

      // Check initial value
      const initialValue = element.value || '';
      if (initialValue.length >= currentStepData.minCharacters) {
        setInputValues(prev => ({ ...prev, inputValue: initialValue }));
        setIsActionReady(true);
      }
    }

    // Cleanup
    return () => {
      if (currentStepData.actionType === 'click') {
        element.removeEventListener('click', handleActionComplete);
      } else if (currentStepData.actionType === 'type') {
        element.removeEventListener('input', handleInputChange);
        element.removeEventListener('keyup', handleInputChange);
        element.removeEventListener('change', handleInputChange);
      }
    };
  }, [currentStep, currentStepData, steps.length, accountId, chapter, supabase, onComplete, setIsVisible, isAdvancing, isActionReady]);

  // Auto-open dropdown for filter step
  useEffect(() => {
    if (currentStepData?.autoOpenDropdown) {
      const timer = setTimeout(() => {
        const dropdownButton = document.querySelector('.brain-sidebar .relative button');
        if (dropdownButton) {
            dropdownButton.click();
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentStep, currentStepData]);

  // Drag handlers
  const handleDragStart = (e) => {
    if (!dragRef.current) return;

    setIsDragging(true);

    const rect = dragRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    setDragOffset({
      x: clientX - rect.left,
      y: clientY - rect.top
    });

    e.preventDefault();
  };

  const handleDragMove = (e) => {
    if (!isDragging || !dragRef.current) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;

    const modalRect = dragRef.current.getBoundingClientRect();
    const modalWidth = modalRect.width;
    const modalHeight = modalRect.height;

    const padding = 20;
    const maxX = window.innerWidth - modalWidth - padding;
    const maxY = window.innerHeight - modalHeight - padding;

    const constrainedX = Math.max(padding, Math.min(maxX, newX));
    const constrainedY = Math.max(padding, Math.min(maxY, newY));

    setDraggedPosition({
      left: constrainedX,
      top: constrainedY,
      transform: 'none',
      position: 'fixed'
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Global drag event listeners
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleDragMove(e);
      const handleEnd = () => handleDragEnd();

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);

      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, dragOffset]);

  // Reset dragged position when step changes
  useEffect(() => {
    setDraggedPosition(null);
  }, [currentStep]);

  const handleNext = () => {
    // Special handling for experiment-while-waiting step - close tutorial completely
    if (currentStepData?.id === 'experiment-while-waiting') {
      handleCompleteAndClose();
      return;
    }

    const nextStep = findNextValidStep(currentStep);
    if (nextStep !== -1) {
      setCurrentStep(nextStep);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      if (accountId && chapter) {
        await supabase
          .from('brain_tutorial_progress')
          .upsert({
            account_id: accountId,
            chapter_id: chapter.id,
            completed_at: new Date().toISOString()
          }, {
            onConflict: 'account_id,chapter_id'
          });
      }
    } catch (error) {
      console.error('Error marking chapter as complete:', error);
    }

    setIsVisible(false);
    setTimeout(() => onComplete(), 300);
  };

  // Complete tutorial and fully close without opening tutorial chapters modal
  const handleCompleteAndClose = async () => {
    try {
      if (accountId && chapter) {
        await supabase
          .from('brain_tutorial_progress')
          .upsert({
            account_id: accountId,
            chapter_id: chapter.id,
            completed_at: new Date().toISOString()
          }, {
            onConflict: 'account_id,chapter_id'
          });
      }
    } catch (error) {
      console.error('Error marking chapter as complete:', error);
    }

    // Close this tutorial overlay immediately
    setIsVisible(false);

    // Don't call onComplete() which might open tutorial chapters modal
    // Instead, just close everything and let user start building
  };

  // Handle tutorial choice selection
  const handleChoiceSelect = (choice) => {
    // Track the user's choice for conditional step display
    setUserChoice(choice.id);

    if (choice.id === 'schedule-call') {
      handleCalBookingClick(choice.id);
    } else if (choice.id === 'continue-building') {
      // Complete tutorial and close everything - user wants to build immediately
      handleCompleteAndClose();
    }
  };

  // Get choice icon component
  const getChoiceIcon = (iconName) => {
    switch (iconName) {
      case 'calendar':
        return Calendar;
      case 'gear':
        return Gear;
      default:
        return ArrowRight;
    }
  };

  // Handle tutorial-specific actions that need to be automated
  const handleTutorialAction = () => {
    const stepId = currentStepData?.id;

    switch (stepId) {
      case 'tutorial-modal-explain-close':
        // Close the tutorial chapters modal
        const tutorialModalClose = document.querySelector('[data-tutorial-chapters-modal="true"] button[aria-label="Close"]');
        if (tutorialModalClose) {
          tutorialModalClose.click();
        }
        break;

      case 'conflicts-panel-explain-close':
        // Close the mixed messages panel the same way the sidebar button does
        const mixedMessagesButton = document.querySelector('.brain-sidebar button[aria-label*="detected"], .brain-sidebar button[aria-label*="conflict"]');
        if (mixedMessagesButton) {
          mixedMessagesButton.click();
        }
        break;

      case 'coverage-panel-explain':
        // Close the analysis panel the same way the sidebar button does
        const coverageButton = document.querySelector('.brain-sidebar button[aria-label*="AI Coverage"], .brain-sidebar button[aria-label*="Coverage"]');
        if (coverageButton) {
          coverageButton.click();
        }
        break;

      default:
        // For other action types, try to find and click the target element
        if (currentStepData?.target) {
          const element = document.querySelector(currentStepData.target);
          if (element) {
            element.click();
          }
        }
        break;
    }

    // Move to next step after performing the action
    setTimeout(() => {
      const nextStep = findNextValidStep(currentStep);
      if (nextStep !== -1) {
        setCurrentStep(nextStep);
      } else {
        handleComplete();
      }
    }, 500);
  };


  const handleBackdropClick = (e) => {
    e.stopPropagation();
  };

  const getSpotlightStyle = () => {
    if (!currentStepData?.target) return {};

    const element = document.querySelector(currentStepData.target);
    if (!element) return {};

    const rect = element.getBoundingClientRect();
    const padding = 8;

    return {
      position: 'fixed',
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
      borderRadius: '12px',
      border: '3px solid rgba(59, 130, 246, 1)',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.3), 0 0 0 9999px rgba(0, 0, 0, 0.4)',
      pointerEvents: 'none',
      zIndex: currentStepData?.actionType === 'type' ? 1001 : 100011,
    };
  };

  // Simple action status text
  const getActionStatusText = () => {
    if (!currentStepData?.requiresAction) return null;

    const { actionType, minCharacters } = currentStepData;

    if (actionType === 'type') {
      if (minCharacters && !isActionReady) {
        return `Type at least ${minCharacters} character${minCharacters > 1 ? 's' : ''}`;
      }
      return isActionReady ? 'Continue' : 'Keep typing...';
    } else if (actionType === 'click') {
      const element = document.querySelector(currentStepData.target);
      if (element) {
        // Check aria-label for specific button types
        const ariaLabel = element.getAttribute('aria-label')?.toLowerCase() || '';

        // Map specific buttons to their icons
        if (ariaLabel.includes('close')) {
          return <><span className="mr-1">Click</span><X className="w-4 h-4" /></>;
        }
        if (ariaLabel.includes('tutorial') || currentStepData.id === 'tutorial-button-intro') {
          return <><span className="mr-1">Click</span><Lightbulb className="w-4 h-4" /></>;
        }
        if (ariaLabel.includes('conflict') || ariaLabel.includes('detected') || currentStepData.id === 'conflicts-button-intro') {
          return <><span className="mr-1">Click</span><ShieldWarning className="w-4 h-4" /></>;
        }
        if (ariaLabel.includes('coverage') || currentStepData.id === 'coverage-button-intro') {
          return <><span className="mr-1">Click</span><ChartBar className="w-4 h-4" /></>;
        }
        if (ariaLabel.includes('assistant') || currentStepData.id === 'ai-assistant-intro') {
          return <><span className="mr-1">Click</span><AndroidLogo className="w-4 h-4" /></>;
        }

        // Get button text content for text buttons
        const text = element.textContent?.trim();
        if (text && text.length > 0 && text.length < 30) {
          return <><span className="mr-1">Click</span><span className="font-medium">"{text}"</span></>;
        }

        // Generic click with icon indicator
        return <><span className="mr-1">Click</span><ArrowRight className="w-4 h-4" /></>;
      }

      return <><span className="mr-1">Click</span><ArrowRight className="w-4 h-4" /></>;
    }

    return 'Complete the action';
  };

  // Intelligent positioning - places modal next to highlighted element
  const getTooltipPosition = () => {
    // Use dragged position if user has moved the modal manually
    if (draggedPosition) {
      return draggedPosition;
    }

    // Center the modal when there's no target or when position is 'center'
    if (!currentStepData?.target || currentStepData?.position === 'center') {
      // Use viewport-relative dimensions for responsive centering
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Dynamic modal dimensions - use viewport percentages for responsiveness
      // max-w-md is roughly 28rem = ~30% of typical desktop, 90% of mobile
      const modalWidthPercent = viewportWidth < 640 ? 0.9 : 0.3; // 90% mobile, 30% desktop
      const modalHeightPercent = 0.4; // Roughly 40% of viewport height

      const modalWidth = viewportWidth * modalWidthPercent;
      const modalHeight = viewportHeight * modalHeightPercent;

      // Calculate center position relative to viewport
      const leftPercent = (50 - (modalWidthPercent * 50)); // 50% minus half modal width
      const topPercent = (50 - (modalHeightPercent * 50)); // 50% minus half modal height

      return { 
        position: 'fixed',
        top: Math.max(2, topPercent) + 'vh', // Use viewport units, min 2vh from edge
        left: Math.max(2, leftPercent) + 'vw', // Use viewport units, min 2vw from edge
        transform: 'none',
        margin: '0'
      };
    }

    const element = document.querySelector(currentStepData.target);
    if (!element) {
      // Fallback to center if element not found - same responsive logic
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const modalWidthPercent = viewportWidth < 640 ? 0.9 : 0.3;
      const modalHeightPercent = 0.4;
      const leftPercent = (50 - (modalWidthPercent * 50));
      const topPercent = (50 - (modalHeightPercent * 50));

      return { 
        position: 'fixed',
        top: Math.max(2, topPercent) + 'vh',
        left: Math.max(2, leftPercent) + 'vw',
        transform: 'none',
        margin: '0'
      };
    }

    const rect = element.getBoundingClientRect();
    const modalWidth = 400; // max-w-md
    const modalHeight = 200; // estimated
    const gap = 20;

    // Simple priority: right > left > below > above
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Try right side first
    if (rect.right + gap + modalWidth <= viewport.width - 20) {
      return {
        position: 'fixed',
        left: rect.right + gap,
        top: Math.max(20, Math.min(rect.top, viewport.height - modalHeight - 20)),
        transform: 'none'
      };
    }

    // Try left side
    if (rect.left - gap - modalWidth >= 20) {
      // Add extra left offset for right-panel tutorial steps
      const extraLeftOffset = ['conflicts-panel-explain-close', 'coverage-panel-explain', 'ai-chat-explain'].includes(currentStepData.id) ? 40 : 0;

      return {
        position: 'fixed',
        left: rect.left - gap - modalWidth - extraLeftOffset,
        top: Math.max(20, Math.min(rect.top, viewport.height - modalHeight - 20)),
        transform: 'none'
      };
    }

    // Try below
    if (rect.bottom + gap + modalHeight <= viewport.height - 20) {
      return {
        position: 'fixed',
        left: Math.max(20, Math.min(rect.left, viewport.width - modalWidth - 20)),
        top: rect.bottom + gap,
        transform: 'none'
      };
    }

    // Try above
    if (rect.top - gap - modalHeight >= 20) {
      return {
        position: 'fixed',
        left: Math.max(20, Math.min(rect.left, viewport.width - modalWidth - 20)),
        top: rect.top - gap - modalHeight,
        transform: 'none'
      };
    }

    // Fallback to bottom-right if no good position found
    return {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      transform: 'none'
    };
  };

  if (!isActive || !isVisible || !chapter || steps.length === 0) return null;

  // Check if tutorial chapters modal is open (recalculated on each render)
  const isTutorialChaptersModalOpen = document.querySelector('[data-tutorial-chapters-modal="true"]');

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0"
        style={{
          zIndex: currentStepData?.actionType === 'type' ? 1000 : 100010,
          pointerEvents: 'none'
        }}
        onClick={currentStepData?.actionType === 'type' ? undefined : handleBackdropClick}
      >
        {/* Spotlight effect */}
        {currentStepData?.target ? (
          <div 
            style={getSpotlightStyle()}
            className="absolute"
          />
        ) : (
          <div 
            className="absolute inset-0 bg-black bg-opacity-40"
            style={{
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Clickable area over highlighted element */}
        {/* Removed: Allow natural interaction within all highlighted areas */}

        {/* Tutorial Content */}
        <motion.div
          ref={dragRef}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ 
            opacity: 1, 
            scale: isDragging ? 1.02 : 1, 
            y: 0 
          }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ delay: 0.1, duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 max-w-md select-none"
          style={{ 
            ...getTooltipPosition(), 
            zIndex: 999999,
            pointerEvents: 'auto',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          {/* Header with Chapter indicator and Close button */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {chapter.id.replace('chapter-', '')}
              </span>
            </div>
            <span className="text-xs text-gray-500 font-medium">
              {chapter.title}
            </span>
            </div>

            {/* Close button */}
            <button
              onClick={onSkip}
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 group"
              aria-label="Close tutorial"
            >
              <X className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center space-x-2 mb-4">
            {steps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "w-2 h-2 rounded-full transition-colors",
                  index === currentStep ? "bg-blue-500" : "bg-gray-300"
                )}
              />
            ))}
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {currentStepData?.title}
          </h3>
          <p className="text-gray-600 text-sm mb-6 leading-relaxed">
            {currentStepData?.content}
          </p>

          {/* Choice buttons for next-steps-choice step */}
          {currentStepData?.showChoices && currentStepData?.choices && (
            <div className="space-y-3 mb-6">
              {currentStepData.choices.map((choice) => {
                const IconComponent = getChoiceIcon(choice.icon);

                return choice.id === 'schedule-call' ? (
                  // Cal.com button for scheduling
                  <button
                    key={choice.id}
                    onClick={handleCalBookingClick}
                    className={cn(
                      "w-full text-left p-4 rounded-xl border-2 transition-all duration-200 group",
                      choice.primary 
                        ? "border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300" 
                        : "border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300"
                    )}
                    disabled={isBookingCall}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        choice.primary ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"
                      )}>
                        {isBookingCall ? (
                          <CircleNotch className="w-5 h-5 animate-spin" />
                        ) : (
                          <IconComponent className="w-5 h-5" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className={cn(
                          "font-medium text-sm",
                          choice.primary ? "text-blue-900" : "text-gray-900"
                        )}>
                          {isBookingCall ? 'Opening Calendar...' : choice.title}
                        </h4>
                        <p className={cn(
                          "text-xs mt-0.5",
                          choice.primary ? "text-blue-700" : "text-gray-600"
                        )}>
                          {choice.description}
                        </p>
                      </div>
                      <ArrowRight className={cn(
                        "w-4 h-4 transition-transform group-hover:translate-x-1",
                        choice.primary ? "text-blue-600" : "text-gray-400"
                      )} />
                    </div>
                  </button>
                ) : (
                  // Regular choice button
                  <button
                    key={choice.id}
                    onClick={() => handleChoiceSelect(choice)}
                    className={cn(
                      "w-full text-left p-4 rounded-xl border-2 transition-all duration-200 group",
                      choice.primary 
                        ? "border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300" 
                        : "border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300"
                    )}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        choice.primary ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"
                      )}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className={cn(
                          "font-medium text-sm",
                          choice.primary ? "text-blue-900" : "text-gray-900"
                        )}>
                          {choice.title}
                        </h4>
                        <p className={cn(
                          "text-xs mt-0.5",
                          choice.primary ? "text-blue-700" : "text-gray-600"
                        )}>
                          {choice.description}
                        </p>
                      </div>
                      <ArrowRight className={cn(
                        "w-4 h-4 transition-transform group-hover:translate-x-1",
                        choice.primary ? "text-blue-600" : "text-gray-400"
                      )} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Navigation - Only show for non-choice steps */}
          {!currentStepData?.showChoices && (
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                currentStep === 0 
                  ? "text-gray-400 cursor-not-allowed" 
                  : "text-gray-600 hover:bg-gray-100"
              )}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            {/* Action vs informational steps */}
            {currentStepData?.requiresAction ? (
              // Check if this is a close action step that needs automation
              ['tutorial-modal-explain-close', 'conflicts-panel-explain-close', 'coverage-panel-explain'].includes(currentStepData.id) ? (
                <button
                  onClick={handleTutorialAction}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                >
                  <span>Next</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  className={cn(
                    "flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-not-allowed",
                    "bg-blue-100 text-blue-400"
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // No action - user must perform the actual action
                  }}
                  disabled={true}
                >
                  {currentStepData.actionType === 'type' ? (
                    <>
                      <span>{getActionStatusText()}</span>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <div className="w-1 h-4 bg-blue-400 animate-pulse rounded-full"></div>
                      </span>
                    </>
                  ) : (
                    getActionStatusText()
                  )}
                </button>
              )
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
              >
                <span>
                  {currentStep === steps.length - 1 ? 'Complete' : currentStepData?.action || 'Next'}
                </span>
                {currentStep === steps.length - 1 ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
          )}

          {/* Navigation for choice steps - only show Previous */}
          {currentStepData?.showChoices && (
            <div className="flex justify-start">
              <button
                onClick={handlePrev}
                disabled={currentStep === 0}
                className={cn(
                  "flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                  currentStep === 0 
                    ? "text-gray-400 cursor-not-allowed" 
                    : "text-gray-600 hover:bg-gray-100"
                )}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default TutorialOverlay; 